/**
 * SyncVaultButton.jsx — iter908 (Feb 2026)
 *
 * Manually triggers POST /api/pathway-vault/rebuild — useful when a
 * patient re-tests after 4 weeks and wants to see immediately how the
 * new lab values changed their KEGG-pathway activations. Closes the loop
 * from Daily Check-in's retest prompt.
 *
 * Also shown on Labs page so the patient can force a resync anytime.
 */
import React, { useState } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/App";

const COPY = {
  bg: {
    idle:     "Обнови Vault от лаборатории",
    running:  "Пресмятам…",
    done:     "Vault обновен",
    fail:     "Грешка при обновяване",
    tooltip:  "Тригерира ново обединяване на лабораторни резултати и активни KEGG пътища. Vault-ът се обновява и когато качиш нов анализ.",
    updated:  (n, p) => `Vault обновен · ${n} състояния · ${p} пътища`,
  },
  en: {
    idle:     "Sync Vault from labs",
    running:  "Recomputing…",
    done:     "Vault synced",
    fail:     "Sync failed",
    tooltip:  "Triggers a fresh reconciliation of lab findings and active KEGG pathways. The vault also auto-rebuilds on any new upload.",
    updated:  (n, p) => `Vault synced · ${n} conditions · ${p} pathways`,
  },
};

export default function SyncVaultButton({
  isDark = false,
  lang = "bg",
  variant = "primary",  // "primary" | "ghost" | "inline"
  testid = "sync-vault-button",
  onSynced,
}) {
  const { API } = useAuth();
  const t = COPY[lang] || COPY.bg;
  const [state, setState] = useState("idle"); // idle | running | done | fail
  const [msg, setMsg] = useState("");

  const handleClick = async () => {
    if (state === "running") return;
    setState("running");
    setMsg("");
    try {
      const res = await axios.post(`${API}/pathway-vault/rebuild`);
      const v = res?.data?.vault || {};
      setMsg(t.updated(v.condition_count ?? 0, v.pathway_count ?? 0));
      setState("done");
      onSynced?.(v);
      setTimeout(() => setState("idle"), 3500);
    } catch (e) {
      setMsg(e?.response?.data?.detail || t.fail);
      setState("fail");
      setTimeout(() => setState("idle"), 4000);
    }
  };

  const label = state === "running" ? t.running
              : state === "done"    ? t.done
              : state === "fail"    ? t.fail
              : t.idle;

  const Icon = state === "done" ? CheckCircle2
             : state === "fail" ? AlertCircle
             : RefreshCw;

  const variantCls = variant === "primary"
    ? (isDark
        ? "bg-gradient-to-r from-emerald-500 to-sky-500 text-white hover:opacity-90"
        : "bg-gradient-to-r from-emerald-500 to-sky-500 text-white hover:opacity-90")
    : variant === "ghost"
    ? (isDark
        ? "border border-slate-700 text-slate-200 hover:bg-slate-800"
        : "border border-slate-300 text-slate-700 hover:bg-slate-100")
    : (isDark
        ? "text-emerald-300 hover:text-emerald-200"
        : "text-emerald-700 hover:text-emerald-800");

  const stateCls = state === "done"
    ? (isDark ? "bg-emerald-500/25 text-emerald-200" : "bg-emerald-100 text-emerald-800")
    : state === "fail"
    ? (isDark ? "bg-rose-500/25 text-rose-200"       : "bg-rose-100 text-rose-800")
    : "";

  return (
    <div className="inline-flex items-center gap-2" data-testid={`${testid}-wrapper`}>
      <button
        onClick={handleClick}
        disabled={state === "running"}
        title={t.tooltip}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition disabled:opacity-70
          ${stateCls || variantCls}`}
      >
        <Icon className={`w-3.5 h-3.5 ${state === "running" ? "animate-spin" : ""}`} />
        {label}
      </button>
      {msg && state !== "running" && (
        <span
          data-testid={`${testid}-msg`}
          className={`text-[11px] ${state === "fail"
            ? (isDark ? "text-rose-300" : "text-rose-600")
            : (isDark ? "text-emerald-300" : "text-emerald-700")}`}
        >
          {msg}
        </span>
      )}
    </div>
  );
}
